/**
 * 审计核心各模块与服务器同步（与风险功能一致：优先服务器，失败则本地）
 */

const API_BASE = '/api/audit-core';
const ALLOWED_KEYS = [
  'audit-plans', 'audit-kanban', 'broiler-internal-control', 'incompatible-duties', 'ippf-auditors', 'audit-groups',
  'risk-matrix', 'internal-control', 'supervision-complaints', 'supervision-tips', 'supervision-rectification', 'supervision-notices',
  'ic-eval-main', 'ic-eval-tests', 'ic-eval-module-groups', 'ic-eval-survey-records',
  'user-management', 'ai-api-config', 'ai-api-configs', 'audit-settings', 'audit-permissions', 'audit-backups', 'audit-logs',
  'project-templates', 'audit-standard-programs', 'audit-workpaper-templates', 'knowledge-regulations', 'knowledge-cases',
  'supervision-topics'
];
const DYNAMIC_KEY_PATTERN = /^[a-z0-9-]{2,100}$/;
const IS_TEST_ENV = (
  (typeof process !== 'undefined' && !!process?.env?.VITEST) ||
  (typeof process !== 'undefined' && process?.env?.NODE_ENV === 'test')
);

function resolveApiUrl(path) {
  if (/^https?:\/\//i.test(path)) return path;
  try {
    if (typeof window !== 'undefined' && window.location?.origin) {
      return new URL(path, window.location.origin).toString();
    }
  } catch {}
  return path;
}

function isAllowedKey(key) {
  return ALLOWED_KEYS.includes(key) || DYNAMIC_KEY_PATTERN.test(String(key || ''));
}

export function toTime(value) {
  const t = Date.parse(value || '');
  return Number.isFinite(t) ? t : 0;
}

export function getDataUpdatedTime(data) {
  if (data == null) return 0;
  if (typeof data !== 'object') return 0;
  const rootTs = Math.max(
    toTime(data.updatedAt),
    toTime(data.lastUpdatedAt),
    toTime(data.createdAt)
  );
  if (!Array.isArray(data.projects)) return rootTs;
  const projectTs = Math.max(
    0,
    ...data.projects.map((p) => Math.max(toTime(p?.updatedAt), toTime(p?.createdAt)))
  );
  return Math.max(rootTs, projectTs);
}

export function pickNewerByTimestamp(localData, serverData, fallback = null) {
  if (serverData == null) return localData ?? fallback;
  if (localData == null) return serverData;
  const localTs = getDataUpdatedTime(localData);
  const serverTs = getDataUpdatedTime(serverData);
  if (localTs === 0 && serverTs === 0) return localData;
  return localTs >= serverTs ? localData : serverData;
}

export function isSaveOk(result) {
  return !!(result && result.ok === true);
}

function getHeaders() {
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * 从服务器加载指定 key 的数据
 * @param {string} key - audit-plans | audit-kanban | broiler-internal-control | incompatible-duties | ippf-auditors | audit-groups
 * @returns {Promise<any>} - 解析后的数据，失败返回 null
 */
export async function loadAuditCore(key) {
  if (!isAllowedKey(key)) return null;
  if (IS_TEST_ENV) return null;
  try {
    const res = await fetch(resolveApiUrl(`${API_BASE}/${key}`), {
      method: 'GET',
      headers: getHeaders(),
      credentials: 'include',
    });
    if (res.ok) return await res.json();
  } catch (_) {}
  return null;
}

/**
 * 保存指定 key 的数据到服务器
 * @param {string} key
 * @param {any} data
 * @returns {Promise<{ ok: boolean; status?: number; message?: string }>}
 */
export async function saveAuditCore(key, data) {
  if (!isAllowedKey(key)) return { ok: false, message: '无效的 key' };
  if (IS_TEST_ENV) return { ok: true, skipped: true };
  try {
    const res = await fetch(resolveApiUrl(`${API_BASE}/${key}`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getHeaders() },
      credentials: 'include',
      body: JSON.stringify({ data }),
    });
    if (res.ok) return { ok: true };
    const body = await res.json().catch(() => ({}));
    const msg = body?.error || res.statusText || `HTTP ${res.status}`;
    if (res.status === 401) return { ok: false, status: 401, message: body?.error || '请先登录系统' };
    if (res.status === 403) {
      return {
        ok: false,
        status: 403,
        message: body?.error || '当前账号无权限保存该配置（通常需要管理员账号）',
      };
    }
    return { ok: false, status: res.status, message: msg };
  } catch (e) {
    const isNetwork = /fetch failed|Failed to fetch|NetworkError|Load failed/i.test(e?.message || '');
    return { ok: false, message: isNetwork ? '网络异常，请检查能否访问服务器' : (e?.message || '请求失败') };
  }
}

export async function ensureAuditCoreSaved(key, data, context = '') {
  const result = await saveAuditCore(key, data);
  if (isSaveOk(result)) return result;
  const prefix = context ? `[${context}] ` : '';
  const message = `${prefix}${result?.message || '保存到服务器失败'}`;
  const err = new Error(message);
  err.result = result;
  throw err;
}

/**
 * 管理员：批量合并法规或案例至审计核心（去重写入，供合同审查等读取）
 * POST /api/audit-core/merge-knowledge-items
 * @param {'knowledge-regulations'|'knowledge-cases'} key
 * @param {unknown[]} items
 */
export async function mergeKnowledgeItems(key, items) {
  if (!['knowledge-regulations', 'knowledge-cases'].includes(key)) {
    return { ok: false, message: '无效的 key' };
  }
  if (!Array.isArray(items) || !items.length) {
    return { ok: false, message: 'items 必须为非空数组' };
  }
  if (IS_TEST_ENV) return { ok: true, skipped: true };
  try {
    const res = await fetch(resolveApiUrl(`${API_BASE}/merge-knowledge-items`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getHeaders() },
      credentials: 'include',
      body: JSON.stringify({ key, items }),
    });
    const body = await res.json().catch(() => ({}));
    if (res.ok) return { ok: true, ...body };
    const msg = body?.error || res.statusText || `HTTP ${res.status}`;
    if (res.status === 403) {
      return {
        ok: false,
        status: 403,
        message: body?.error || '需要系统管理员权限（role=admin）',
      };
    }
    return { ok: false, status: res.status, message: msg };
  } catch (e) {
    return { ok: false, message: e?.message || '请求失败' };
  }
}
